<template>
    <NuxtLayout name="main-small" :dark="true">
        <div class="container flex items-start !mt-[80px]" :class="css.container">
            <!-- * 菜单 -->
            <div class="sticky top-[40px] t-border-uicolor4 rounded-[4px] px-[40px] py-[24px] w-[280px]" :class="css.menus">
                <div class="text-[20px] text-emphas leading-[36px] font-bold">{{ $t("MainContent") }}</div>
                <div class="flex flex-col items-start mt-[4px]">
                    <template v-for="(menu, index) in menus">
                        <div
                            class="text-[14px] leading-[30px] underline-offset-4 t-delay"
                            :class="[css.menu, id == menu.id && css.active]"
                            @click="methods.move(index)">
                            {{ menu.name }}
                        </div>
                    </template>
                </div>
            </div>

            <!-- * 内容 -->
            <div class="flex-1 ml-[96px]" ref="sections">
                <section class="text-[36px] text-emphas leading-[52px] font-bold">{{ $t("SequenceProcessor") }}</section>
                <section class="mt-[64px]" data-key="section">
                    <div :class="css.h2">{{ $t("ToolIntroductionAndBackground") }}</div>
                    <div :class="css.content">
                        <div class="text-emphas">{{ $t("SequenceProcessorTutorials.D1") }}</div>
                    </div>
                </section>

                <section class="mt-[72px]" data-key="section">
                    <div :class="css.h2">{{ $t("DetailedOperatingSteps") }}</div>
                    <div :class="css.content">
                        <div :class="css.h3">{{ $t("SequenceProcessorTutorials.T1") }}</div>
                        <i18n-t tag="div" scope="global" keypath="OpenABrowserNavigateTo">
                            <template v-slot:name>{{ $t("SequenceProcessor") }}</template>
                            <template v-slot:href>
                                <ProjectRoute :class="styles.assets" :to="{ name: 'SequenceProcessor' }">
                                    https://rddc.tsinghua-gd.org/zh/tool/sequence-processor
                                </ProjectRoute>
                            </template>
                        </i18n-t>

                        <img v-src="1" />
                        <div :class="css.h3" class="!mt-[32px]">{{ $t("SequenceProcessorTutorials.T2") }}</div>
                        <div :class="css.context">{{ $t("SequenceProcessorTutorials.D2") }}</div>
                        <img v-src="2" />
                        <div :class="css.h3" class="!mt-[32px]">{{ $t("SequenceProcessorTutorials.T3") }}</div>
                        <div :class="css.context">{{ $t("SequenceProcessorTutorials.D3") }}</div>
                        <img v-src="3" />
                        <div :class="css.h3" class="!mt-[32px]">{{ $t("SequenceProcessorTutorials.T4") }}</div>
                        <div :class="css.ordered">
                            <div :class="css.nos"></div>
                            <div :class="css.context">
                                <span class="font-bold text-emphas">{{ $t("SequenceProcessorTutorials.D4") }}</span>
                                <span>{{ $t("SequenceProcessorTutorials.D5") }}</span>
                            </div>

                            <div :class="css.nos"></div>
                            <div :class="css.context">
                                <span class="font-bold text-emphas">{{ $t("SequenceProcessorTutorials.D6") }}</span>
                                <span>{{ $t("SequenceProcessorTutorials.D7") }}</span>
                            </div>

                            <div :class="css.nos"></div>
                            <div :class="css.context">
                                <span class="font-bold text-emphas">{{ $t("SequenceProcessorTutorials.D8") }}</span>
                                <span>{{ $t("SequenceProcessorTutorials.D9") }}</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="mt-[72px]" data-key="section">
                    <div :class="css.h2">{{ $t("AuxiliaryFunctionsResources") }}</div>
                    <div :class="css.content">
                        <div :class="css.context">{{ $t("SequenceProcessorTutorials.D10") }}</div>
                        <img v-src="4" />
                    </div>
                </section>

                <section class="mt-[72px]" data-key="section">
                    <div :class="css.h2">{{ $t("Applications") }}</div>
                    <div :class="css.content">
                        <div :class="css.ordered">
                            <div :class="css.nos"></div>
                            <div :class="css.context">
                                <span class="font-bold text-emphas">{{ useColon($t("ResearchScenario")) }}</span>
                                <span>{{ $t("SequenceProcessorTutorials.D12") }}</span>
                            </div>

                            <div :class="css.nos"></div>
                            <div :class="css.context">
                                <span class="font-bold text-emphas">{{ useColon($t("EducationScenario")) }}</span>
                                <span>{{ $t("SequenceProcessorTutorials.D14") }}</span>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </NuxtLayout>
</template>

<script lang="ts" setup>
//* Vue
import type { Directive } from "vue";
//* 全局函数
import { Nodes } from "@/assets/js";
//* 样式
import css from "../index.module.less";
import styles from "@/assets/style/src/index.module.less";
//* 图片资源导入
const images = import.meta.glob("./src/images/*/*.webp", { query: "url", import: "default" });

//* 插件
const i18n = useI18n();
//* 响应式变量
const id = ref<string>("#summary");
const sections = ref<HTMLDialogElement>();
const menus = ref<{ id: string; name: string }[]>([
    { id: "#summary", name: i18n.t("ToolIntroductionAndBackground") },
    { id: "#steps", name: i18n.t("DetailedOperatingSteps") },
    { id: "#helper", name: i18n.t("AuxiliaryFunctionsResources") },
    { id: "#sample", name: i18n.t("Applications") },
]);

//* 指令
const vSrc: Directive<HTMLImageElement, string | number> = {
    created: (_node, response, vnode) => {
        images[`./src/images/${i18n.locale.value}/${response.value.toString()}.webp`]?.().then((src) => {
            vnode.el.src = src;
        });
    },
};

//* 函数列表
const methods = {
    move: (index: number) => {
        //* 数据更新
        id.value = menus.value[index].id;
        //* 页面滚动
        const nodes = sections.value?.querySelectorAll("[data-key='section']") || [];
        const node = nodes[index] as HTMLDivElement;
        if (node) {
            window.scrollTo({ top: Nodes.offsetTop(0, node) - 40, behavior: "smooth" });
        }
    },

    // scroll: () => {
    //     //* 数据初始化
    //     const offsetY = window.scrollY;
    //     const header = document.querySelector("header")?.offsetHeight || 0;
    //     const nodes = sections.value?.querySelectorAll("[data-key='section']") || [];
    //     //* 索引初始化
    //     id.value = menus.value[0].id;
    //     //* 循环设置索引
    //     for (let i = 0; i < nodes.length; i++) {
    //         const node = nodes[i] as HTMLDivElement;
    //         if (Nodes.offsetTop(0, node) - header - 40 > offsetY) break;
    //         else {
    //             id.value = menus.value[i].id;
    //         }
    //     }
    // },
};

//* 组件卸载前移除滚动事件
// onBeforeUnmount(() => removeEventListener("scroll", methods.scroll, true));
//* 组件挂载后注册滚动事件
// onMounted(() => {
//     methods.scroll(), addEventListener("scroll", methods.scroll);
// });
</script>
